import { useFormikContext } from 'formik';
import { useCurrentUser } from 'hooks/auth/useCurrentUser';
import { useSnackbar } from 'notistack';
import { useCallback, useState } from 'react';

import {
  useGetApplicationFormJobInfoQuery,
  useCreateFieldGlassCandidateIntroductionMutation,
  useSubmitFieldGlassCandidateIntroductionMutation as useSubmitFieldGlassCandidateIntroductionMutationBase,
  useUploadFileMutation,
  Job,
  JobMatch,
  FieldGlassCandidateIntroduction,
  FileTypeEnum,
  useUpdateFieldGlassCandidateIntroductionMutation,
  GetFieldGlassSubmissionStatusDocument,
} from '@libs/graphql-types';
import { FormikSubmit, modelPath } from '@libs/helpers/form';

import {
  mapFormToFieldGlassInput,
  mapFormToFieldGlassSaveInput,
  mapApplicationFormDataToInitialValues,
} from './mappers';
import { ApplicationFormState } from './types';

export const useLoadApplicationData = ({
  jobId,
  matchId,
}: {
  jobId: string;
  matchId: string;
}) => {
  const { data, loading } = useGetApplicationFormJobInfoQuery({
    variables: { job_id: jobId, match_id: matchId },
    skip: !jobId || !matchId,
  });

  const { initialValues } = mapApplicationFormDataToInitialValues({
    job: data?.job as Job,
    candidateIntroduction: data?.fieldGlassCandidateIntroductionByJobMatchId as
      | FieldGlassCandidateIntroduction
      | undefined,
    matchId,
  });

  return {
    initialValues,
    job: data?.job,
    isLoading: loading,
  };
};

export const useSaveApplication = (
  jobId: string,
  jobData: Job,
  jobMatch: JobMatch,
) => {
  const [loading, setLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const [createFieldGlassCandidate] =
    useCreateFieldGlassCandidateIntroductionMutation();
  const [updateFieldGlassCandidateIntroduction] =
    useUpdateFieldGlassCandidateIntroductionMutation();
  const { getData } = useCurrentUser();
  const currentUserId = getData().data?.currentCompanyUser?.id!;

  const onSubmit: FormikSubmit<ApplicationFormState> = useCallback(
    async (values: ApplicationFormState, formikHelpers) => {
      try {
        setLoading(true);

        if (values.introductionId) {
          const introduction = mapFormToFieldGlassSaveInput({
            formData: values,
            jobData,
            jobMatch,
            currentUserId,
          });

          await updateFieldGlassCandidateIntroduction({
            variables: {
              introduction,
              introduction_id: values.introductionId,
            },
            onCompleted: () => {
              enqueueSnackbar('Application saved successfully!', {
                variant: 'success',
              });
            },
          });

          return;
        }

        const introduction = mapFormToFieldGlassInput({
          formData: values,
          jobData,
          jobMatch,
          currentUserId,
        });

        await createFieldGlassCandidate({
          variables: {
            introduction,
            job_match_id: jobMatch.id,
          },
          onCompleted: (data) => {
            // openSubmittingModal();
            enqueueSnackbar('Application saved successfully!', {
              variant: 'success',
            });
            formikHelpers.setFieldValue(
              modelPath<ApplicationFormState>((i) => i.introductionId),
              data.createFieldGlassCandidateIntroduction.id,
            );
          },
        });
      } catch (error) {
        console.error('Error submitting application:', error);
        enqueueSnackbar(
          error instanceof Error
            ? error.message
            : 'Failed to save application. Please try again.',
          {
            variant: 'error',
          },
        );
      } finally {
        setLoading(false);
      }
    },
    [
      jobData,
      jobMatch,
      currentUserId,
      createFieldGlassCandidate,
      updateFieldGlassCandidateIntroduction,
      enqueueSnackbar,
    ],
  );

  return {
    loading,
    onSubmit,
  };
};

export const useResumeUpload = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { setFieldValue, values, validateField, setFieldTouched } =
    useFormikContext<ApplicationFormState>();
  const [uploadFile, { loading }] = useUploadFileMutation();

  const handleResumeUpload = useCallback(
    async (files: File[]) => {
      if (!files.length) return;

      try {
        const file = files[0];

        const { data } = await uploadFile({
          variables: {
            file,
            file_type: FileTypeEnum.CandidateIntroductionCv,
          },
        });

        if (data?.uploadFile) {
          // Update form state with the uploaded file information
          setFieldValue(
            modelPath<ApplicationFormState>((i) => i.resume.newCvUpload),
            data.uploadFile,
          );
          setFieldTouched(
            modelPath<ApplicationFormState>((i) => i.resume.newCvUpload),
            true,
          );
          setFieldTouched(
            modelPath<ApplicationFormState>((i) => i.resume.filename),
            true,
          );
          // Validate the resume field after upload to trigger validation
          setTimeout(() => {
            validateField(modelPath<ApplicationFormState>((i) => i.resume));
          }, 0);

          enqueueSnackbar('Resume uploaded successfully!', {
            variant: 'success',
          });
        }
      } catch (error) {
        console.error('Error uploading resume:', error);
        enqueueSnackbar('Failed to upload resume. Please try again.', {
          variant: 'error',
        });
      }
    },
    [
      uploadFile,
      setFieldValue,
      setFieldTouched,
      validateField,
      enqueueSnackbar,
    ],
  );

  const handleResetUpload = useCallback(() => {
    setFieldValue(
      modelPath<ApplicationFormState>((i) => i.resume.newCvUpload),
      null,
    );
    setFieldTouched(
      modelPath<ApplicationFormState>((i) => i.resume),
      true,
    );
    // Validate the resume field after reset to trigger validation
    setTimeout(() => {
      validateField(modelPath<ApplicationFormState>((i) => i.resume));
    }, 0);
  }, [setFieldValue, setFieldTouched, validateField]);

  return {
    handleResumeUpload,
    handleResetUpload,
    loading,
    newCvUpload: values.resume.newCvUpload,
  };
};

export const useSubmitFieldGlassCandidateIntroductionMutation = (): {
  loading: boolean;
  submitIntroduction: (introductionId: string) => Promise<void>;
} => {
  const [loading, setLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const [submitFieldGlassCandidateIntroduction] =
    useSubmitFieldGlassCandidateIntroductionMutationBase({
      onCompleted: () => {
        // enqueueSnackbar('Application submitted successfully!', {
        //   variant: 'success',
        // });
      },
    });

  const submitIntroduction = useCallback(
    async (introductionId: string): Promise<void> => {
      try {
        setLoading(true);

        const { errors } = await submitFieldGlassCandidateIntroduction({
          variables: {
            introduction_id: introductionId,
          },
          refetchQueries: [
            {
              query: GetFieldGlassSubmissionStatusDocument,
              variables: {
                match_id: introductionId,
              },
            },
          ],
        });

        if (errors) {
          throw new Error(errors[0].message);
        }
      } catch (error) {
        console.error('Error submitting introduction:', error);
        enqueueSnackbar(
          error instanceof Error
            ? error.message
            : 'Failed to submit introduction. Please try again.',
          {
            variant: 'error',
          },
        );
      } finally {
        setLoading(false);
      }
    },
    [submitFieldGlassCandidateIntroduction, enqueueSnackbar],
  );

  return {
    loading,
    submitIntroduction,
  };
};

export { useSubmissionStatusPolling } from './hooks/useSubmissionStatusPolling';
